/**
 * TourControlStrip — top strip that lives directly under the DemoTopBar
 * on the VEROOGLE canvas and exposes the AUTO / MANUAL buttons the
 * sales rep uses to launch the guided tour. Sticks to the top of the
 * viewport so a rep can relaunch mid-pitch.
 */
import React from "react";
import { useGuidedTour } from "@/lib/guidedTour";
import { PlayCircle, Hand, Square, Mic, Clapperboard, Music2, VolumeX, Sparkles } from "lucide-react";

import RerecordAllButton from "./RerecordAllButton";
import VoicePickerButton from "./VoicePickerButton";

const JUMPS = [
  ["savings", "Overview"], ["phase1", "Phase 1"], ["phase2", "Phase 2"], ["phase3", "Phase 3"],
  ["phase4", "Phase 4"], ["phase5", "Phase 5"], ["phase6", "Phase 6"], ["recap-final", "Wrap-up"],
];

export default function TourControlStrip({ jumps = JUMPS, isAdmin = false, onRerecorded, onReplayIntro, onPlayFinale, pulseStart = false, musicOn = true, onToggleMusic }) {
  const pulse = pulseStart ? " ring-2 ring-emerald-400/80 ring-offset-2 ring-offset-[#0A192F] animate-pulse" : "";
  const { isActive, start, stop, mode, step, script } = useGuidedTour();
  const jump = (id) => {
    const idx = script.findIndex((b) => b.id === id);
    if (idx >= 0) start(isActive && mode !== "paused" ? mode : "auto", idx);
  };
  const currentId = isActive ? script[step]?.id : null;
  const MusicIcon = musicOn ? Music2 : VolumeX;

  return (
    <div
      data-testid="veroogle-tour-strip"
      className={`w-full border-b border-white/8 backdrop-blur-md ${isActive ? "sticky top-0 z-[95] bg-[#0B0F19]/95" : "bg-[#0B0F19]/80"}`}
    >
      <div className="max-w-7xl mx-auto flex flex-wrap items-center justify-between gap-x-4 gap-y-2 px-6 md:px-10 py-2.5">
        <div className="flex items-center gap-2 text-slate-300">
          <Mic className="w-3.5 h-3.5 text-brand-cyan" />
          <span className="font-mono text-[10px] tracking-[0.18em] uppercase text-slate-400">
            Guided Demo
          </span>
          {isActive && (
            <span className="font-mono text-[10px] tracking-[0.18em] uppercase text-brand-cyan ml-2">
              · {mode.toUpperCase()} · Step {step + 1} / {script.length}
            </span>
          )}
        </div>

        {jumps.length > 0 && <div className={`order-last w-full flex-wrap items-center gap-1.5 ${isActive ? "hidden sm:flex" : "flex"}`} data-testid="veroogle-tour-jumps">
          <span className="font-mono text-[9px] tracking-[0.2em] uppercase text-slate-500 mr-1">Jump to</span>
          {jumps.map(([id, label, disabled]) => (
            <button key={id} type="button" onClick={() => jump(id)} disabled={disabled} data-testid={`veroogle-tour-jump-${id}`}
              className={`px-2.5 py-1 rounded-full border font-mono text-[10px] tracking-[0.12em] uppercase transition-colors disabled:opacity-35 disabled:pointer-events-none ${currentId === id ? "border-brand-cyan text-brand-cyan bg-brand-cyan/10" : "border-white/10 text-slate-400 hover:text-white hover:border-white/30"}`}>
              {label}
            </button>
          ))}
          {onPlayFinale && (
            <button type="button" onClick={() => { if (isActive) stop(); onPlayFinale(); }} data-testid="veroogle-tour-jump-finale"
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full border border-emerald-400/40 text-emerald-300 hover:bg-emerald-500/10 font-mono text-[10px] tracking-[0.12em] uppercase transition-colors">
              <Sparkles className="w-3 h-3" /> Finale
            </button>
          )}
          {onToggleMusic && <button type="button" onClick={onToggleMusic} data-testid="veroogle-tour-music-toggle" aria-label="Toggle tour music"
            className={`ml-auto inline-flex items-center gap-1 px-2.5 py-1 rounded-full border font-mono text-[10px] tracking-[0.12em] uppercase transition-colors ${musicOn ? "border-emerald-400/40 text-emerald-300" : "border-white/10 text-slate-500"}`}>
            <MusicIcon className="w-3 h-3" /> Music {musicOn ? "on" : "off"}
          </button>}
        </div>}
        {!isActive ? (
          <div className="flex flex-wrap items-center gap-2">
            {onReplayIntro && <button
              type="button"
              onClick={onReplayIntro}
              data-testid="veroogle-intro-replay"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/5 hover:bg-emerald-500/10 text-slate-300 hover:text-emerald-300 border border-white/10 hover:border-emerald-500/40 font-mono text-[11px] font-semibold tracking-[0.14em] uppercase whitespace-nowrap transition-colors"
            >
              <Clapperboard className="w-3.5 h-3.5" /> Replay intro
            </button>}
            {isAdmin && <VoicePickerButton onRerecorded={onRerecorded} />}
            {isAdmin && <RerecordAllButton onDone={onRerecorded} />}
            <button
              type="button"
              onClick={() => start("auto")}
              data-testid="veroogle-tour-start-auto"
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gradient-to-r from-brand-blue to-brand-cyan hover:brightness-110 text-navy-950 font-mono text-[11px] font-semibold tracking-[0.14em] uppercase whitespace-nowrap transition shadow-lg shadow-cyan-500/20${pulse}`}
            >
              <PlayCircle className="w-3.5 h-3.5" /> Auto Demo
            </button>
            <button
              type="button"
              onClick={() => start("manual")}
              data-testid="veroogle-tour-start-manual"
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/5 hover:bg-brand-cyan/10 text-slate-200 hover:text-brand-cyan border border-white/10 hover:border-brand-cyan/50 font-mono text-[11px] font-semibold tracking-[0.14em] uppercase whitespace-nowrap transition${pulse}`}
            >
              <Hand className="w-3.5 h-3.5" /> Manual Demo
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={stop}
            data-testid="veroogle-tour-strip-stop"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-rose-500/10 hover:bg-rose-500/20 text-rose-300 border border-rose-500/40 font-mono text-[11px] font-semibold tracking-[0.14em] uppercase whitespace-nowrap transition"
          >
            <Square className="w-3.5 h-3.5" /> End Tour
          </button>
        )}
      </div>
    </div>
  );
}
